import { holdOpener, openerOf } from "@baton/ui";
import { useCallback, useRef } from "react";

/**
 * `contracts/design-system.md` § Focus (2026-09-27): a menu whose row opens a dialog must not hand the
 * focus back to its trigger as it closes, because the dialog places the focus itself and the menu's return
 * would pull it out from behind the dialog (a picker the dialog opened open would close, a field
 * would lose its caret). `opens` wraps a row's action so the menu's `onCloseAutoFocus` then keeps the
 * focus where the dialog put it; a row that opens nothing, and a menu closed by `Esc` or a click
 * outside, returns the focus to the trigger as every menu does. The dialog gives the focus back to
 * the menu's trigger when it closes (`packages/ui` `useReturnFocus`), even a dialog whose code arrives
 * after the menu has closed, the row handing that trigger over through `holdOpener`.
 */
export interface MenuOpensDialog {
  readonly opens: (open: () => void) => () => void;
  /**
   * A row whose action puts a field or a popover in the page rather than a dialog of its own (a
   * state's "Add state after this" and Change colour…, 2026-09-30): the action runs once the menu has
   * closed, since while it is open its focus scope takes back any focus outside it, and a field that
   * saves or cancels on blur would be gone before the person could type.
   */
  readonly afterClose: (action: () => void) => () => void;
  readonly onCloseAutoFocus: (event: Event) => void;
}

export function useMenuOpensDialog(): MenuOpensDialog {
  const opening = useRef(false);
  const pending = useRef<(() => void) | null>(null);
  const opens = useCallback(
    (open: () => void) => () => {
      opening.current = true;
      // The row is gone once the menu closes; the dialog returns the focus to the menu's trigger, which the row hands over now.
      holdOpener(openerOf(document.activeElement));
      open();
    },
    [],
  );
  const afterClose = useCallback(
    (action: () => void) => () => {
      opening.current = true;
      pending.current = action;
    },
    [],
  );
  const onCloseAutoFocus = useCallback((event: Event) => {
    if (!opening.current) return;
    opening.current = false;
    event.preventDefault();
    const action = pending.current;
    pending.current = null;
    action?.();
  }, []);
  return { opens, afterClose, onCloseAutoFocus };
}
